import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  createOrder,
  getGrouponById,
  getPaymentChannels,
  payOrder,
  type ChannelInfo,
  type GrouponDeal,
  type PaymentChannelCode,
} from '@/lib/api';
import { formatPrice } from '@/lib/format';
import { useTk } from '@/lib/i18n';
import { useAuth } from '@/lib/use-auth';

export function CheckoutPage(): React.ReactElement {
  const tk = useTk();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const dealId = searchParams.get('dealId') ?? '';
  const auth = useAuth();

  const [deal, setDeal] = useState<GrouponDeal | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [channels, setChannels] = useState<ChannelInfo[]>([]);
  const [selectedChannel, setSelectedChannel] = useState<PaymentChannelCode>('mock');
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!dealId) return;
    const ac = new AbortController();
    getGrouponById(dealId, ac.signal)
      .then(setDeal)
      .catch(() => setDeal(null));
    return () => ac.abort();
  }, [dealId]);

  useEffect(() => {
    const token = auth.token;
    if (!token) return;
    const ac = new AbortController();
    getPaymentChannels(token, ac.signal)
      .then(({ channels: chs }) => {
        setChannels(chs);
        if (chs.length > 0) {
          setSelectedChannel(chs[0]!.channelCode);
        }
      })
      .catch(() => setChannels([]));
    return () => ac.abort();
  }, [auth.token]);

  async function handleSubmit(): Promise<void> {
    if (!auth.token || !dealId) return;
    setSubmitting(true);
    setErrorMessage(null);
    try {
      const { order } = await createOrder({ dealId, quantity, channelCode: selectedChannel }, auth.token);
      const { intent } = await payOrder(order.id, selectedChannel, auth.token);
      if (!intent.nonce) {
        throw new Error('Payment intent missing return nonce');
      }
      const qs = new URLSearchParams({
        nonce: intent.nonce,
        channel: selectedChannel,
      });
      navigate(`/orders/${encodeURIComponent(order.id)}?${qs.toString()}`, { replace: true });
    } catch (err) {
      const apiErr = err as Error & { code?: string };
      setErrorMessage(`${apiErr.code ?? 'ERROR'}: ${apiErr.message}`);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="min-h-screen px-6 py-8 space-y-4 pb-24">
      <header className="flex items-center justify-between">
        <Link to={`/groupons/${dealId}`} className="text-[var(--tg-link)] text-sm">
          ← {tk('nav.groupon')}
        </Link>
        <h1 className="text-xl font-semibold">{tk('checkout.title')}</h1>
        <span />
      </header>

      {deal ? (
        <>
          <section className="rounded-lg border border-neutral-200 bg-white p-4 space-y-2 text-sm">
            <h3 className="font-medium text-base">{deal.titleZh}</h3>
            <p className="text-xs text-[var(--tg-hint)]">{deal.merchant.nameZh}</p>
            <div className="flex items-baseline justify-between">
              <span className="text-[var(--tg-hint)]">{tk('checkout.unit_price')}</span>
              <span>{formatPrice(deal.priceCents, deal.currency)}</span>
            </div>
          </section>

          <section className="rounded-lg border border-neutral-200 bg-white p-4 space-y-3 text-sm">
            <div className="flex items-center justify-between">
              <span>{tk('checkout.quantity')}</span>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                  className="w-8 h-8 rounded border border-neutral-300"
                >
                  -
                </button>
                <span className="w-8 text-center">{quantity}</span>
                <button
                  type="button"
                  onClick={() => setQuantity((q) => Math.min(deal.perUserLimit ?? 5, q + 1))}
                  className="w-8 h-8 rounded border border-neutral-300"
                >
                  +
                </button>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-[var(--tg-hint)]">{tk('checkout.subtotal')}</span>
              <span className="text-brand-500 font-semibold">
                {formatPrice(BigInt(deal.priceCents) * BigInt(quantity), deal.currency)}
              </span>
            </div>
          </section>

          {channels.length > 0 ? (
            <section className="rounded-lg border border-neutral-200 bg-white p-4 space-y-3 text-sm">
              <h2 className="font-medium">{tk('checkout.payment_channel')}</h2>
              <div className="space-y-2">
                {channels.map((channel) => {
                  const selected = selectedChannel === channel.channelCode;
                  return (
                    <button
                      key={channel.channelCode}
                      type="button"
                      disabled={!channel.enabled || submitting}
                      onClick={() => setSelectedChannel(channel.channelCode)}
                      className={`w-full rounded-xl border p-3 text-left transition-colors ${
                        selected
                          ? 'border-brand-500 bg-brand-50'
                          : 'border-neutral-200 bg-white'
                      } ${channel.enabled ? 'hover:border-brand-500' : 'opacity-60'}`}
                      aria-pressed={selected}
                    >
                      <span className="flex items-center justify-between gap-3">
                        <span className="font-medium">{channel.displayName}</span>
                        {!channel.enabled ? (
                          <span className="rounded-full bg-neutral-100 px-2 py-0.5 text-[11px] text-neutral-500">
                            {tk('checkout.channel_disabled')}
                          </span>
                        ) : null}
                      </span>
                    </button>
                  );
                })}
              </div>
            </section>
          ) : null}

          {errorMessage ? (
            <p className="text-xs text-danger-700 break-all">{errorMessage}</p>
          ) : null}

          <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-neutral-200 px-6 py-3">
            <button
              type="button"
              disabled={submitting || auth.status !== 'authenticated'}
              onClick={() => void handleSubmit()}
              className="block w-full rounded-lg bg-brand-500 text-white text-center py-3 font-medium disabled:bg-neutral-300"
            >
              {submitting ? tk('checkout.submitting') : tk('checkout.submit')}
            </button>
          </div>
        </>
      ) : (
        <div className="rounded-lg border border-dashed border-neutral-300 p-10 text-center text-neutral-500">
          {tk('common.loading')}
        </div>
      )}
    </main>
  );
}
